import React, { useState, useEffect } from "react";
import { Sparkles, FileUp, MessageSquareText, ArrowRight, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * WelcomeBanner. onboarding card shown on Dashboard for users whose
 * account is <72 hours old and who haven't created their first case yet.
 *
 * Acknowledges the stress (tenancy issues are emotional). Guides to a single
 * next action: "Add my place". Dismissable, but reappears until the user
 * actually starts a case.
 *
 * The CTA calls `onStart` (provided by Dashboard) which opens the same
 * NewCaseModal as the header "New case" button. We do NOT route to
 * /cases/new, that path is captured by /cases/:id and hangs on
 * "Loading case..." because there is no case with id "new".
 */
export default function WelcomeBanner({ caseCount = 0, onStart }) {
 const { user } = useAuth();
 const [dismissed, setDismissed] = useState(false);

 // Always reset on mount so the user sees it again after a refresh if they
 // still haven't started. but we ALSO honour a session-level dismiss.
 useEffect(() => {
 setDismissed(sessionStorage.getItem("welcome_dismissed") === "1");
 }, []);

 if (!user) return null;
 if (caseCount > 0) return null; // they've started. no need to nudge
 if (dismissed) return null;

 // Don't show to long-time users who haven't created a case (they probably
 // dismissed and don't want it again)
 if (user.created_at) {
 const ageDays = (Date.now() - new Date(user.created_at).getTime()) / 86400000;
 if (ageDays > 14) return null;
 }

 const dismiss = () => {
 sessionStorage.setItem("welcome_dismissed", "1");
 setDismissed(true);
 };

 return (
 <div className="card relative mb-6 border-l-4 border-l-[#1B6C62] bg-gradient-to-br from-[#E6F5F2]/50 to-transparent" data-testid="welcome-banner">
 <button
 onClick={dismiss}
 className="absolute top-3 right-3 text-[#9CA3AF] hover:text-[#0D2B52] tap-target"
 aria-label="Dismiss welcome message"
 data-testid="welcome-banner-dismiss"
 >
 <X size={16} strokeWidth={1.5} />
 </button>

 <div className="label-mono text-[10px] text-[#1B6C62] mb-2 flex items-center gap-1.5">
 <Sparkles size={11} strokeWidth={1.75} />
 <span>Welcome, {user.name?.split(" ")[0] || "friend"}</span>
 </div>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-2 leading-tight">
 Welcome, we&rsquo;ve got your back from day one.
 </h2>
 <p className="text-[14px] text-[#5a6b7a] leading-relaxed mb-5 max-w-2xl">
 Renting in Australia is hard. TenantTrail is on your side, quietly
 looking after your paperwork, your bond, your renting record. Start
 with one quick step below: add the place you&rsquo;re renting now and
 we&rsquo;ll do the rest in the background.
 </p>

 <div className="grid sm:grid-cols-3 gap-3 mb-5">
 <div className="flex items-start gap-2.5 text-[13px]" data-testid="welcome-step-1">
 <div className="w-7 h-7 shrink-0 rounded-full bg-[#1B6C62] text-white flex items-center justify-center font-mono text-[12px]">1</div>
 <div>
 <div className="font-medium text-[#0D2B52]">Add your place</div>
 <p className="text-[12px] text-[#5a6b7a] leading-snug">Just the address and your state, takes 30 seconds.</p>
 </div>
 </div>
 <div className="flex items-start gap-2.5 text-[13px]" data-testid="welcome-step-2">
 <div className="w-7 h-7 shrink-0 rounded-full bg-[#1B6C62] text-white flex items-center justify-center font-mono text-[12px]">2</div>
 <div>
 <div className="font-medium text-[#0D2B52]">Drop in your lease</div>
 <p className="text-[12px] text-[#5a6b7a] leading-snug">PDF or a phone photo, we&rsquo;ll find the key dates.</p>
 </div>
 </div>
 <div className="flex items-start gap-2.5 text-[13px]" data-testid="welcome-step-3">
 <div className="w-7 h-7 shrink-0 rounded-full bg-[#1B6C62] text-white flex items-center justify-center font-mono text-[12px]">3</div>
 <div>
 <div className="font-medium text-[#0D2B52]">Chat with Compass</div>
 <p className="text-[12px] text-[#5a6b7a] leading-snug">Ask anything in plain English. Like a savvy friend.</p>
 </div>
 </div>
 </div>

 <button
 type="button"
 onClick={() => {
 if (typeof onStart === "function") {
 onStart();
 } else {
 // Fallback for any caller that forgot to pass onStart.
 // Sends them to the Dashboard with the auto-open flag.
 window.location.href = "/dashboard?new=1";
 }
 }}
 className="btn-teal inline-flex items-center gap-2 text-sm tap-target px-5"
 data-testid="welcome-banner-start-cta"
 >
 Add my place <ArrowRight size={14} strokeWidth={1.75} />
 </button>
 </div>
 );
}
